import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import BundeslandOptions from '../components/BundeslandOptions';
import api from '../services/api';
import toast from 'react-hot-toast';
import { ProfilUnterseite, SchalterZeile } from '../components/profil/ProfilUnterseite';

interface NotificationSettingsData {
  eventChangesEnabled: boolean;
  reminderEnabled: boolean;
  /** Anmeldung geöffnet bzw. Preisstufe endet. */
  registrationEnabled: boolean;
  /** Jemand schickt eine Freundschaftsanfrage. */
  friendRequestsEnabled: boolean;
  /** Rundmail mit Neuigkeiten von Radsport Events (V204). */
  newsEnabled: boolean;
  /** Montags-Mail: was diese Woche in der Region stattfindet. */
  digestEnabled: boolean;
  /** Donnerstags-Mail: was in der Region neu eingetragen wurde. */
  newEventEnabled: boolean;
  /** Region für BEIDE Regions-Mails (Feldname historisch vom Digest). */
  digestBundesland: string | null;
}

export default function NotificationSettings() {
  const { isAuthenticated } = useAuth();
  const [settings, setSettings] = useState<NotificationSettingsData>({
    eventChangesEnabled: true,
    reminderEnabled: true,
    registrationEnabled: true,
    friendRequestsEnabled: true,
    newsEnabled: true,
    digestEnabled: false,
    newEventEnabled: false,
    digestBundesland: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    api.get<NotificationSettingsData>('/notifications/settings')
      .then(r => setSettings(s => ({ ...s, ...r.data })))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  const save = async (updated: NotificationSettingsData) => {
    setSettings(updated);
    setSaving(true);
    try {
      await api.put('/notifications/settings', updated);
    } catch {
      setSettings(settings);
      toast.error('Einstellung konnte nicht gespeichert werden');
    } finally {
      setSaving(false);
    }
  };

  // Die Regions-Mails gibt es nur mit Region (der Server verschickt ohne keine und nimmt die
  // Einstellung auch nicht an). Deshalb steht die Region ueber den Schaltern, und ohne sie
  // sind die beiden Schalter gesperrt — ein Schalter, der sich umlegen laesst und dann
  // abgewiesen wird, ist die schlechtere Fuehrung.
  const hasRegion = !!settings.digestBundesland;

  const handleToggle = (key: 'eventChangesEnabled' | 'reminderEnabled' | 'registrationEnabled' | 'friendRequestsEnabled' | 'newsEnabled' | 'digestEnabled' | 'newEventEnabled') => {
    if ((key === 'digestEnabled' || key === 'newEventEnabled') && !hasRegion) return;
    save({ ...settings, [key]: !settings[key] });
  };

  const handleBundeslandChange = (bundesland: string) => {
    if (!bundesland) return;
    save({ ...settings, digestBundesland: bundesland });
  };

  return (
    <ProfilUnterseite titel="Benachrichtigungen" aktion={saving ? <span className="pf-unterseite-status">Wird gespeichert …</span> : undefined}>
      {loading ? (
        <p className="pf-hinweis">Wird geladen …</p>
      ) : (
        <>
          {/* Die zwei Regions-Mails. Bewusst ohne Fachbegriffe beschrieben: was drin steht,
              wann sie kommt, wann sie ausbleibt. Beide teilen sich EINE Region, und ohne Region
              sind beide Schalter gesperrt: der Server verschickt ohne keine. */}
          <section className="pf-gruppe" aria-labelledby="nb-region">
            <h2 id="nb-region" className="pf-gruppe-titel">Mails zu deiner Region</h2>
            <label className="pf-feld" htmlFor="digest-region">
              <span className="pf-feld-label">Deine Region</span>
              <select id="digest-region" className="pf-eingabe" value={settings.digestBundesland ?? ''}
                onChange={e => handleBundeslandChange(e.target.value)}>
                {/* Keine Option "alle Regionen": ohne Filter kaeme alles weltweit. */}
                <option value="" disabled>Region wählen …</option>
                <BundeslandOptions />
              </select>
              <span className="pf-feld-hilfe">
                {hasRegion ? 'Gilt für beide Mails darunter.' : 'Wähle zuerst eine Region, dann lassen sich beide Mails einschalten.'}
              </span>
            </label>
            <SchalterZeile titel="Montags: Events dieser Woche" disabled={!hasRegion}
              text="Alle Events von Montag bis Sonntag in deiner Region. Steht nichts an, kommt keine Mail."
              checked={settings.digestEnabled} onChange={() => handleToggle('digestEnabled')} />
            <SchalterZeile titel="Donnerstags: neu eingetragene Events" disabled={!hasRegion}
              text="Was in der letzten Woche neu dazukam, egal wann es stattfindet. Gut für Anmeldungen, die früh voll sind."
              checked={settings.newEventEnabled} onChange={() => handleToggle('newEventEnabled')} />
          </section>

          <section className="pf-gruppe" aria-labelledby="nb-events">
            <h2 id="nb-events" className="pf-gruppe-titel">Mails zu deinen Events</h2>
            <SchalterZeile titel="Änderungen" text="Datum, Ort oder Strecken ändern sich, oder das Event fällt aus."
              checked={settings.eventChangesEnabled} onChange={() => handleToggle('eventChangesEnabled')} />
            <SchalterZeile titel="Erinnerung 7 Tage vorher" text="Eine Woche vor dem Event."
              checked={settings.reminderEnabled} onChange={() => handleToggle('reminderEnabled')} />
            <SchalterZeile titel="Anmeldung" text="Wenn die Anmeldung öffnet und bevor der Preis steigt."
              checked={settings.registrationEnabled} onChange={() => handleToggle('registrationEnabled')} />
          </section>

          <section className="pf-gruppe" aria-labelledby="nb-freunde">
            <h2 id="nb-freunde" className="pf-gruppe-titel">Freunde</h2>
            <SchalterZeile titel="Freundschaftsanfragen" text="Wenn dir jemand eine Anfrage schickt."
              checked={settings.friendRequestsEnabled} onChange={() => handleToggle('friendRequestsEnabled')} />
          </section>

          <section className="pf-gruppe" aria-labelledby="nb-neuigkeiten">
            <h2 id="nb-neuigkeiten" className="pf-gruppe-titel">Radsport Events</h2>
            <SchalterZeile titel="Neuigkeiten" text="Neue Funktionen und Aktionen wie die Winterchallenge, etwa einmal im Monat."
              checked={settings.newsEnabled} onChange={() => handleToggle('newsEnabled')} />
          </section>

          <p className="pf-hinweis">Die Mails gehen an die Adresse aus deinem Profil.</p>
        </>
      )}
    </ProfilUnterseite>
  );
}
